import React, { useState, useEffect } from 'react';
import { 
    Plus, Trash2, CheckCircle, AlertTriangle, ArrowLeft, Search, Check 
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const NouvelleReservation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = Boolean(editId);

  const [modeReservation, setModeReservation] = useState('INDIVIDUELLE'); // 'INDIVIDUELLE', 'GROUPE'

  const [reservantNom, setReservantNom] = useState('');
  const [reservantTel, setReservantTel] = useState('');
  const [clientNom, setClientNom] = useState('');

  const [beneficiairesParChambre, setBeneficiairesParChambre] = useState({});

  const aujourdhui = new Date().toISOString().split('T')[0];

  const getLendemain = (dateStr) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const [dateArrivee, setDateArrivee] = useState(aujourdhui);
  const [dateDepart, setDateDepart] = useState(getLendemain(aujourdhui));
  
  const [toutesChambres, setToutesChambres] = useState([]);
  const [chambresDisponibles, setChambresDisponibles] = useState([]);
  const [chambresOccupees, setChambresOccupees] = useState([]);
  const [chambresSelectionnees, setChambresSelectionnees] = useState([]); 
  
  // 💡 Gestion des composants et prix négociés par chambre (clé: chambre_id)
  const [composantsParChambre, setComposantsParChambre] = useState({});
  const [selectionComposants, setSelectionComposants] = useState({});

  const [rechercheDispo, setRechercheDispo] = useState('');
  const [rechercheOccupee, setRechercheOccupee] = useState('');

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchChambresEtDisponibilites = async (arr = dateArrivee, dep = dateDepart) => {
    if (!arr || !dep || arr >= dep) return;

    try {
      setLoading(true);
      const urlDispo = isEditMode 
        ? `/hebergement/reservations/disponibles?arrivee=${arr}&depart=${dep}&exclude_reservation_id=${editId}`
        : `/hebergement/reservations/disponibles?arrivee=${arr}&depart=${dep}`;

      const [chambresPhysiquesRes, disposRes] = await Promise.all([
        API.get('/hebergement/chambres').catch(() => ({ data: [] })),
        API.get(urlDispo).catch(() => ({ data: [] }))
      ]);

      const rawChambres = chambresPhysiquesRes.data;
      const listChambres = Array.isArray(rawChambres) ? rawChambres : (rawChambres?.data || rawChambres?.chambres || []);
      setToutesChambres(listChambres);

      const rawDispos = disposRes.data;
      let listDispos = [];
      let backendOccupees = [];

      if (Array.isArray(rawDispos)) {
        listDispos = rawDispos;
      } else if (rawDispos && typeof rawDispos === 'object') {
        listDispos = rawDispos.disponibles || rawDispos.data || [];
        backendOccupees = rawDispos.occupees || [];
      }

      const mapChambres = new Map(listChambres.map(ch => [ch.id, ch]));

      const chambresDisposCompletes = listDispos.map(ch => {
        const id = ch.id || ch.chambre_id;
        const matchingChambre = mapChambres.get(id) || ch;
        return {
          ...matchingChambre,
          turnover_message: ch.turnover_message || null
        };
      });

      const chambresOccupeesCompletes = backendOccupees.map(o => {
        const id = o.id || o.chambre_id;
        const match = mapChambres.get(id);
        return {
          ...o,
          numero: o.numero || match?.numero,
          chambre_statut: o.chambre_statut || match?.statut_actuel || match?.statut || 'RESERVE'
        };
      });

      setChambresDisponibles(chambresDisposCompletes);
      setChambresOccupees(chambresOccupeesCompletes);
    } catch (err) {
      console.error("Erreur chargement chambres:", err);
      showToast("Erreur de chargement des chambres", "error");
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial unique + mode édition
  useEffect(() => {
    fetchChambresEtDisponibilites(dateArrivee, dateDepart);

    if (isEditMode) {
      const chargerReservationPourModification = async () => {
        try {
          const response = await API.get(`/hebergement/reservations/${editId}`);
          const resData = response.data;
          if (resData) {
            const typeRes = (resData.type_reservation || 'INDIVIDUELLE').toUpperCase();
            setModeReservation(typeRes);

            setReservantNom(resData.reservant_nom || '');
            setReservantTel(resData.reservant_telephone || '');
            setClientNom(resData.client_principal_nom || '');
            
            const arr = resData.date_arrivee_prevue ? resData.date_arrivee_prevue.split('T')[0] : aujourdhui;
            const dep = resData.date_depart_prevue ? resData.date_depart_prevue.split('T')[0] : getLendemain(aujourdhui);
            
            setDateArrivee(arr);
            setDateDepart(dep);
            fetchChambresEtDisponibilites(arr, dep);

            if (resData.chambres && Array.isArray(resData.chambres)) {
              const formattedItems = [];
              const newComposantsMap = {};
              const newSelectionMap = {};

              for (const c of resData.chambres) {
                const chId = c.chambre_id || c.id;
                formattedItems.push({
                  chambre_id: chId,
                  numero: c.numero || c.chambre_numero || 'N/C',
                  tarif_nuit: c.tarif_nuit || 0,
                  client_nom: c.client_nom || '',
                  date_check_in: c.date_check_in ? c.date_check_in.split('T')[0] : arr,
                  date_check_out: c.date_check_out ? c.date_check_out.split('T')[0] : dep
                });

                // Restitution des composants figés ou des composants de la chambre
                if (c.composants && Array.isArray(c.composants) && c.composants.length > 0) {
                  newComposantsMap[chId] = c.composants;
                  const sel = {};
                  c.composants.forEach((comp, idx) => {
                    const k = comp.composant_id || comp.id || idx;
                    sel[k] = true;
                  });
                  newSelectionMap[chId] = sel;
                } else {
                  try {
                    const compRes = await API.get(`/hebergement/chambres/${chId}`);
                    const itemsComp = compRes.data?.items || compRes.data?.composants || [];
                    newComposantsMap[chId] = itemsComp;
                    const sel = {};
                    itemsComp.forEach((comp, idx) => {
                      const k = comp.id || idx;
                      sel[k] = true;
                    });
                    newSelectionMap[chId] = sel;
                  } catch (errComp) {
                    console.error("Erreur chargement composants chambre edit:", errComp);
                  }
                }
              }

              setComposantsParChambre(newComposantsMap);
              setSelectionComposants(newSelectionMap);
              setChambresSelectionnees(formattedItems);
            }
          }
        } catch (err) {
          console.error("Erreur chargement réservation à modifier:", err);
          showToast("Impossible de charger les données de la réservation", "error");
        }
      };
      chargerReservationPourModification();
    }
  }, [editId]);

  const handleArriveeChange = (e) => {
    const nouvelleArrivee = e.target.value;
    setDateArrivee(nouvelleArrivee);
    let nouvelleDepart = dateDepart;
    if (dateDepart <= nouvelleArrivee) {
      nouvelleDepart = getLendemain(nouvelleArrivee);
      setDateDepart(nouvelleDepart);
    }
    fetchChambresEtDisponibilites(nouvelleArrivee, nouvelleDepart);
  };

  const handleDepartChange = (e) => {
    const nouveauDepart = e.target.value;
    setDateDepart(nouveauDepart);
    fetchChambresEtDisponibilites(dateArrivee, nouveauDepart);
  };

  const chambresDisponiblesFiltrees = chambresDisponibles.filter(ch => 
    (ch.numero && ch.numero.toLowerCase().includes(rechercheDispo.toLowerCase())) ||
    (ch.categorie_nom && ch.categorie_nom.toLowerCase().includes(rechercheDispo.toLowerCase()))
  );

  const chambresOccupeesFiltrees = chambresOccupees.filter(occ => 
    (occ.numero && occ.numero.toLowerCase().includes(rechercheOccupee.toLowerCase())) ||
    (occ.date_liberation && occ.date_liberation.toLowerCase().includes(rechercheOccupee.toLowerCase()))
  );

  const handleTelChange = (e) => {
    const valeur = e.target.value;
    if (/^[0-9+]*$/.test(valeur)) {
      setReservantTel(valeur);
    }
  };

  const handleBeneficiaireChange = (chambreId, valeur) => {
    setBeneficiairesParChambre(prev => ({
      ...prev,
      [chambreId]: valeur
    }));
  };

  const handleAddChambre = async (chambre) => {
    if (!dateArrivee || !dateDepart || dateArrivee >= dateDepart) {
      showToast("Veuillez renseigner un intervalle de dates valide (minimum 1 nuit)", "error");
      return;
    }

    let nomOccupant = '';
    if (modeReservation === 'GROUPE') {
      nomOccupant = (beneficiairesParChambre[chambre.id] || '').trim();
      if (!nomOccupant) {
        showToast(`Veuillez renseigner le nom du bénéficiaire pour la chambre ${chambre.numero}`, "error");
        return;
      }
    } else {
      nomOccupant = (clientNom || reservantNom).trim();
      if (!nomOccupant) {
        showToast("Veuillez renseigner le nom du client final", "error");
        return;
      }
    }
    
    if (modeReservation !== 'GROUPE' && chambresSelectionnees.length >= 1) {
      showToast("Ce type de réservation ne concerne qu'une seule chambre.", "error");
      return;
    }

    if (chambresSelectionnees.some(item => item.chambre_id === chambre.id)) {
      showToast("Cette chambre est déjà sélectionnée", "error");
      return;
    }

    try {
      const resComp = await API.get(`/hebergement/chambres/${chambre.id}`);
      const itemsComp = resComp.data?.items || resComp.data?.composants || [];
      
      setComposantsParChambre(prev => ({ ...prev, [chambre.id]: itemsComp }));
      
      const initialSelection = {};
      itemsComp.forEach((comp, idx) => {
        const k = comp.id || idx;
        initialSelection[k] = true;
      });
      setSelectionComposants(prev => ({ ...prev, [chambre.id]: initialSelection }));

      const tarifCalcule = itemsComp.reduce((acc, comp) => {
        return acc + (Number(comp.prix_unitaire || comp.prix_defaut || comp.prix || 0) * Number(comp.quantite || 1));
      }, 0) || chambre.tarif_base || chambre.tarif_nuit || 0;

      setChambresSelectionnees([
        ...chambresSelectionnees,
        {
          chambre_id: chambre.id,
          numero: chambre.numero,
          tarif_nuit: tarifCalcule,
          client_nom: nomOccupant.toUpperCase(),
          date_check_in: dateArrivee,  
          date_check_out: dateDepart
        }
      ]);
    } catch (err) {
      console.error("Erreur chargement composants chambre:", err);
      setChambresSelectionnees([
        ...chambresSelectionnees,
        {
          chambre_id: chambre.id,
          numero: chambre.numero,
          tarif_nuit: chambre.tarif_base || chambre.tarif_nuit || 0,
          client_nom: nomOccupant.toUpperCase(),
          date_check_in: dateArrivee,  
          date_check_out: dateDepart
        }
      ]);
    }

    if (modeReservation === 'GROUPE') {
      setBeneficiairesParChambre(prev => ({ ...prev, [chambre.id]: '' }));
    }
  };

  const handleRemoveChambre = (chambre_id) => {
    setChambresSelectionnees(chambresSelectionnees.filter(item => item.chambre_id !== chambre_id));
  };

  const handleItemChange = (chambre_id, field, val) => {
    setChambresSelectionnees(chambresSelectionnees.map(item => {
      if (item.chambre_id === chambre_id) {
        let updated = { ...item, [field]: field === 'tarif_nuit' ? (parseFloat(val) || 0) : val };
        
        if (field === 'date_check_in' && updated.date_check_out <= val) {
          updated.date_check_out = getLendemain(val);
        }
        return updated;
      }
      return item;
    }));
  };

  const handleToggleComposant = (chambreId, compKey, isChecked) => {
    setSelectionComposants(prev => {
      const chambreSelection = { ...(prev[chambreId] || {}), [compKey]: isChecked };
      
      const listeComposants = composantsParChambre[chambreId] || [];
      let nouveauTarif = 0;
      listeComposants.forEach((comp, idx) => {
        const k = comp.id || idx;
        const estCoche = chambreSelection[k] !== false;
        if (estCoche) {
          nouveauTarif += (Number(comp.prix_unitaire || comp.prix_defaut || comp.prix || 0) * Number(comp.quantite || 1));
        }
      });

      setChambresSelectionnees(items => items.map(it => {
        if (it.chambre_id === chambreId) {
          return { ...it, tarif_nuit: nouveauTarif };
        }
        return it;
      }));

      return { ...prev, [chambreId]: chambreSelection };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reservantNom.trim() || !dateArrivee || !dateDepart) {
      showToast("Veuillez remplir tous les champs obligatoires", "error");
      return;
    }
    
    if (!reservantTel || reservantTel.trim() === '') {
      showToast("Le numéro de téléphone du réservant est obligatoire.", "error");
      return;
    }

    if (modeReservation === 'INDIVIDUELLE' && !clientNom.trim()) {
      showToast("Veuillez renseigner le nom du client final", "error");
      return;
    }
    if (dateArrivee >= dateDepart) {
      showToast("La date de départ globale doit être strictement supérieure à l'arrivée", "error");
      return;
    }
    if (chambresSelectionnees.length === 0) {
      showToast("Veuillez sélectionner au moins une chambre", "error");
      return;
    }

    for (const c of chambresSelectionnees) {
      if (c.date_check_in >= c.date_check_out) {
        showToast(`La chambre N° ${c.numero} doit avoir une durée d'au moins 1 nuit (Départ > Arrivée).`, "error");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        type_reservation: modeReservation, 
        reservant_nom: reservantNom.trim().toUpperCase(),
        reservant_telephone: reservantTel.trim(),
        client_principal_nom: (modeReservation === 'INDIVIDUELLE' ? clientNom : reservantNom).trim().toUpperCase(),
        date_arrivee_prevue: dateArrivee,
        date_depart_prevue: dateDepart,
        chambres: chambresSelectionnees.map(c => {
          const listeComposants = composantsParChambre[c.chambre_id] || [];
          const selectionChambre = selectionComposants[c.chambre_id] || {};

          const composantsActifs = listeComposants
            .map((comp, idx) => {
              const compKey = comp.id || idx;
              const estCoche = selectionChambre[compKey] !== false;
              if (!estCoche) return null;

              return {
                composant_id: comp.id || comp.composant_id || null,
                libelle: comp.designation || comp.libelle || comp.nom || 'Prestation',
                prix_unitaire: Number(comp.prix_unitaire || comp.prix_defaut || comp.prix || 0),
                soumis_tva: comp.soumis_tva !== undefined ? Number(comp.soumis_tva) : 1,
                taux_tva: comp.taux_tva !== undefined ? Number(comp.taux_tva) : 18.0
              };
            })
            .filter(Boolean);

          return {
            chambre_id: c.chambre_id,
            tarif_nuit: c.tarif_nuit,
            client_nom: (c.client_nom || reservantNom).trim().toUpperCase(),
            client_telephone: reservantTel.trim(),
            date_check_in: c.date_check_in,  
            date_check_out: c.date_check_out,
            composants: composantsActifs
          };
        })
      };

      if (isEditMode) {
        await API.put(`/hebergement/reservations/${editId}`, payload);
        showToast("Réservation mise à jour avec succès !");
      } else {
        await API.post('/hebergement/reservations', payload);
        showToast("Réservation enregistrée avec succès !");
      }

      // 🔄 Rester sur place et réinitialiser le formulaire
      setReservantNom('');
      setReservantTel('');
      setClientNom('');
      setBeneficiairesParChambre({});
      setDateArrivee(aujourdhui);
      setDateDepart(getLendemain(aujourdhui));
      setChambresSelectionnees([]);
      setComposantsParChambre({});
      setSelectionComposants({});
      await fetchChambresEtDisponibilites(aujourdhui, getLendemain(aujourdhui));
    } catch (err) {
      console.error("Erreur enregistrement réservation:", err);
      showToast(err.response?.data?.error || "Erreur lors de l'enregistrement de la réservation", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, height: '100vh', overflowY: 'hidden', background: '#f1f5f9', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        
            <div>
              <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#1e293b' }}>
                {isEditMode 
                  ? `✏️ MODIFIER LA RÉSERVATION #${editId}` 
                  : (modeReservation === 'INDIVIDUELLE' ? '👤 NOUVELLE RÉSERVATION INDIVIDUELLE' : '👥 NOUVELLE RÉSERVATION DE GROUPE')}
              </h1>
              <div style={{ color: '#64748b', fontSize: '12px' }}>
                Gestion des séjours et nuitées hôtelières
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={() => navigate(-1)} style={btnSecondaryStyle}>Annuler</button>
            <button type="submit" form="reservation-form" disabled={loading} style={btnSubmitStyle}>
              {loading ? 'Enregistrement...' : (isEditMode ? 'Mettre à jour' : 'Valider la Réservation')}
            </button>
          </div>
        </header>

        <div style={{ padding: '20px 30px', width: '100%', maxWidth: '1600px', margin: '0 auto', boxSizing: 'border-box', flex: 1, overflowY: 'auto' }}>
          
          <form id="reservation-form" onSubmit={handleSubmit}>
            
            <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '20px', marginBottom: '20px' }}>
              
              <div style={{ ...contentCardStyle, padding: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label style={{ ...labelStyle, marginBottom: '8px' }}>MODE DE SÉJOUR</label>
                <select
                  value={modeReservation}
                  onChange={(e) => setModeReservation(e.target.value)}
                  style={{ ...inputStyle, marginTop: 0, fontWeight: '800', color: '#1e293b', background: '#f8fafc', cursor: 'pointer' }}
                >
                  <option value="INDIVIDUELLE">👤 Individuelle</option>
                  <option value="GROUPE">👥 Groupe</option>
                </select>
              </div>

              <div style={{ ...contentCardStyle, padding: '15px 20px' }}>
                <h3 style={{ fontSize: '12px', marginBottom: '10px', fontWeight: '800', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  1. Identification & Période Globale (Par défaut)
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: modeReservation === 'INDIVIDUELLE' ? '1.2fr 1fr 1.2fr 1fr 1fr' : '1.5fr 1fr 1.2fr 1.2fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>NOM DU RÉSERVANT</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Nom (qui appelle)..."
                      value={reservantNom} 
                      onChange={(e) => setReservantNom(e.target.value)} 
                      required 
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, color: '#b91c1c' }}>TÉLÉPHONE *</label>
                    <input 
                      type="text" 
                      style={{ ...inputStyle, borderColor: !reservantTel ? '#ef4444' : '#cbd5e1' }} 
                      placeholder="Ex: 90000000"
                      value={reservantTel} 
                      onChange={handleTelChange}
                      required 
                    />
                  </div>
                  
                  {modeReservation === 'INDIVIDUELLE' && (
                    <div>
                      <label style={labelStyle}>NOM DU CLIENT FINAL</label>
                      <input 
                        type="text" 
                        style={inputStyle} 
                        placeholder="Bénéficiaire..."
                        value={clientNom} 
                        onChange={(e) => setClientNom(e.target.value)} 
                        required={modeReservation === 'INDIVIDUELLE'} 
                      />
                    </div>
                  )}

                  <div>
                    <label style={labelStyle}>ARRIVÉE GLOBALE</label>
                    <input 
                      type="date" 
                      style={inputStyle} 
                      min={aujourdhui}
                      value={dateArrivee} 
                      onChange={handleArriveeChange} 
                      required 
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>DÉPART GLOBAL (Min. Arrivée + 1j)</label>
                    <input 
                      type="date" 
                      style={inputStyle} 
                      min={getLendemain(dateArrivee)}
                      value={dateDepart} 
                      onChange={handleDepartChange} 
                      required 
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* LISTES DES CHAMBRES */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              
              {/* Chambres Disponibles */}
              <div style={{ ...contentCardStyle, padding: '15px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#059669', margin: 0 }}>
                    2. Chambres Disponibles ({chambresDisponiblesFiltrees.length})
                  </h3>
                  <div style={{ position: 'relative', width: '160px' }}>
                    <Search size={13} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input 
                      type="text"
                      placeholder="Rechercher..."
                      value={rechercheDispo}
                      onChange={(e) => setRechercheDispo(e.target.value)}
                      style={{ ...inputStyle, marginTop: 0, padding: '4px 8px 4px 26px', fontSize: '11px' }}
                    />
                  </div>
                </div>
                <div style={{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '4px' }}>
                  {chambresDisponiblesFiltrees.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '12px', textAlign: 'center', padding: '30px' }}>Aucune chambre disponible trouvée.</p>
                  ) : (
                    chambresDisponiblesFiltrees.map(ch => {
                      const estSelectionnee = chambresSelectionnees.some(item => item.chambre_id === ch.id);
                      const valBeneficiaire = beneficiairesParChambre[ch.id] || '';

                      return (
                        <div key={ch.id} style={{ display: 'flex', flexDirection: 'column', padding: '8px 12px', background: estSelectionnee ? '#f8fafc' : '#f0fdf4', borderRadius: '6px', border: `1px solid ${estSelectionnee ? '#e2e8f0' : '#bbf7d0'}`, gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ minWidth: '110px' }}>
                              <strong style={{ fontSize: '12px', color: estSelectionnee ? '#94a3b8' : '#065f46' }}>Chambre {ch.numero}</strong>
                              <div style={{ fontSize: '10px', color: estSelectionnee ? '#cbd5e1' : '#047857' }}>{ch.categorie_nom || 'Standard'} • <strong>{Number(ch.tarif_base || ch.tarif_nuit || 0).toLocaleString()} F</strong></div>
                            </div>

                            {modeReservation === 'GROUPE' && !estSelectionnee && (
                              <div style={{ flex: 1, margin: '0 10px' }}>
                                <input 
                                  type="text"
                                  placeholder="Nom du bénéficiaire..."
                                  value={valBeneficiaire}
                                  onChange={(e) => handleBeneficiaireChange(ch.id, e.target.value)}
                                  style={{ ...inputStyle, marginTop: 0, padding: '5px 8px', fontSize: '11px', background: 'white' }}
                                />
                              </div>
                            )}

                            <button 
                              type="button" 
                              disabled={estSelectionnee}
                              onClick={() => handleAddChambre(ch)}
                              style={{ 
                                ...btnPrimaryStyle, 
                                padding: '6px 10px', 
                                fontSize: '11px', 
                                background: estSelectionnee ? '#94a3b8' : '#059669',
                                cursor: estSelectionnee ? 'not-allowed' : 'pointer',
                                opacity: estSelectionnee ? 0.7 : 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {estSelectionnee ? <><Check size={12} /> Ajoutée</> : <><Plus size={12} /> Ajouter</>}
                            </button>
                          </div>

                          {ch.turnover_message && !estSelectionnee && (
                            <div style={{ fontSize: '9px', background: '#fff1f2', color: '#e11d48', padding: '4px 8px', borderRadius: '4px', fontWeight: '800', border: '1px solid #fecdd3', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              ⚠️ {ch.turnover_message}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Chambres Réservées / Bloquées */}
              <div style={{ ...contentCardStyle, padding: '15px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#d97706', margin: 0 }}>
                    Chambres Réservées / Bloquées ({chambresOccupeesFiltrees.length})
                  </h3>
                  <div style={{ position: 'relative', width: '160px' }}>
                    <Search size={13} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input 
                      type="text"
                      placeholder="Rechercher..."
                      value={rechercheOccupee}
                      onChange={(e) => setRechercheOccupee(e.target.value)}
                      style={{ ...inputStyle, marginTop: 0, padding: '4px 8px 4px 26px', fontSize: '11px' }}
                    />
                  </div>
                </div>
                <div style={{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '4px' }}>
                  {chambresOccupeesFiltrees.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: '12px', textAlign: 'center', padding: '30px' }}>Aucune chambre bloquée sur cette période.</p>
                  ) : (
                    chambresOccupeesFiltrees.map(occ => {
                      const dateLiberationStr = occ.date_liberation ? occ.date_liberation.split('T')[0] : '';
                      return (
                        <div key={occ.id || occ.numero} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fde68a' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <strong style={{ fontSize: '12px', color: '#92400e' }}>Chambre {occ.numero}</strong>
                              <span style={{ fontSize: '9px', background: '#d97706', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: '900', letterSpacing: '0.3px' }}>
                                📌 OCCUPÉE / RÉSERVÉE
                              </span>
                            </div>
                            <div style={{ fontSize: '10px', color: '#b45309', marginTop: '2px' }}>
                              Du <strong>{occ.date_debut ? occ.date_debut.split('T')[0] : 'N/C'}</strong> au <strong>{dateLiberationStr || 'N/C'}</strong>
                            </div>
                          </div>
                          <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '4px 8px', borderRadius: '4px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                            {occ.chambre_statut ? occ.chambre_statut.replace(/_/g, ' ') : 'RESERVE'}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

            </div>

            {/* CHAMBRES RETENUES & COMPOSITION TARIFAIRE */}
            <div style={{ ...contentCardStyle, padding: '15px 20px', marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: '800', color: '#1e293b', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  3. Chambre(s) Retenue(s), Occupant(s) & Composition Tarifaire ({chambresSelectionnees.length})
                </h3>
                <span style={{ fontSize: '10px', color: '#64748b' }}>Minimum 1 nuit requise par chambre (Départ &gt; Arrivée)</span>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px', paddingRight: '4px' }}>
                {chambresSelectionnees.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: '12px', textAlign: 'center', padding: '25px' }}>
                    Veuillez sélectionner une chambre dans la liste des disponibles ci-dessus pour l'ajouter au séjour.
                  </p>
                ) : (
                  chambresSelectionnees.map(item => {
                    const composantsChambre = composantsParChambre[item.chambre_id] || [];
                    const chambreSelection = selectionComposants[item.chambre_id] || {};

                    return (
                      <div key={item.chambre_id} style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '15px' }}>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1fr 1fr auto', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                          <div>
                            <strong style={{ fontSize: '13px', color: '#1e40af' }}>Chambre N° {item.numero}</strong>
                          </div>
                          <div>
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#475569', display: 'block', marginBottom: '2px' }}>NOM DE L'OCCUPANT</label>
                            <input 
                              type="text" 
                              style={{ ...inputStyle, marginTop: 0, padding: '5px 8px', background: 'white' }}
                              value={item.client_nom}
                              onChange={(e) => handleItemChange(item.chambre_id, 'client_nom', e.target.value)}
                              placeholder="Nom du client..."
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#475569', display: 'block', marginBottom: '2px' }}>ARRIVÉE CHAMBRE</label>
                            <input 
                              type="date" 
                              style={{ ...inputStyle, marginTop: 0, padding: '5px 8px', background: 'white' }}
                              value={item.date_check_in}
                              onChange={(e) => handleItemChange(item.chambre_id, 'date_check_in', e.target.value)}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '9px', fontWeight: '900', color: '#475569', display: 'block', marginBottom: '2px' }}>DÉPART CHAMBRE (Min. Arrivée + 1j)</label>
                            <input 
                              type="date" 
                              style={{ ...inputStyle, marginTop: 0, padding: '5px 8px', background: 'white' }}
                              value={item.date_check_out}
                              min={getLendemain(item.date_check_in)}
                              onChange={(e) => handleItemChange(item.chambre_id, 'date_check_out', e.target.value)}
                            />
                          </div>
                          <div>
                            <button 
                              type="button" 
                              onClick={() => handleRemoveChambre(item.chambre_id)}
                              style={{ background: '#fee2e2', border: 'none', color: '#b91c1c', padding: '6px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '12px' }}
                              title="Retirer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {composantsChambre.length > 0 && (
                          <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                            <div style={{ fontWeight: '800', fontSize: '11px', color: '#1e293b', marginBottom: '8px' }}>
                              📋 Composition tarifaire (Décochez pour accorder une remise) :
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                  <tr style={{ background: '#f1f5f9', color: '#475569', textAlign: 'left' }}>
                                    <th style={{ padding: '6px', width: '40px', textAlign: 'center' }}>Inclure</th>
                                    <th style={{ padding: '6px' }}>Désignation / Élément</th>
                                    <th style={{ padding: '6px' }}>Quantité</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Prix Unitaire</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {composantsChambre.map((comp, idx) => {
                                    const compKey = comp.id || idx;
                                    const estCoche = chambreSelection[compKey] !== false;
                                    const designation = comp.designation || comp.libelle || comp.nom || 'Prestation';
                                    const prixUnit = Number(comp.prix_unitaire || comp.prix_defaut || comp.prix || 0);

                                    return (
                                      <tr key={compKey} style={{ borderBottom: '1px solid #f1f5f9', background: estCoche ? 'white' : '#fef2f2' }}>
                                        <td style={{ padding: '6px', textAlign: 'center' }}>
                                          <input 
                                            type="checkbox"
                                            checked={estCoche}
                                            onChange={(e) => handleToggleComposant(item.chambre_id, compKey, e.target.checked)}
                                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                                          />
                                        </td>
                                        <td style={{ padding: '6px', fontWeight: '700', color: estCoche ? '#1e293b' : '#94a3b8', textDecoration: estCoche ? 'none' : 'line-through' }}>
                                          {designation}
                                        </td>
                                        <td style={{ padding: '6px', color: '#475569' }}>{comp.quantite || comp.quantite_incluse || 1}</td>
                                        <td style={{ padding: '6px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>
                                          {prixUnit.toLocaleString()} F
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '10px', gap: '8px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '900', color: '#2563eb' }}>Tarif Nuit Négocié / Modifiable pour cette chambre :</label>
                          <input 
                            type="number"
                            value={item.tarif_nuit}
                            onChange={(e) => handleItemChange(item.chambre_id, 'tarif_nuit', e.target.value)}
                            style={{ width: '130px', padding: '6px', borderRadius: '4px', border: '1px solid #2563eb', fontWeight: '900', color: '#2563eb', fontSize: '12px', textAlign: 'right', background: 'white' }}
                          />
                          <span style={{ fontSize: '11px', fontWeight: 'bold' }}>F</span>
                        </div>

                      </div>
                    );
                  })
                )}
              </div>
            </div>

          </form>
        </div>
      </main>

      <style>{`@keyframes slideInToast { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
};

const headerStyle = { background: 'white', padding: '15px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const contentCardStyle = { background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px -1px rgba(0,0,0,0.03)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' };
const btnSecondaryStyle = { border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#475569', background: 'white' };
const btnSubmitStyle = { padding: '8px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '800', fontSize: '12px', boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)' };
const backBtnStyle = { background: 'none', border: 'none', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontWeight: '700', fontSize: '12px' };
const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px', marginTop: '3px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '9px', fontWeight: '900', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' };
const toastContainerStyle = { position: 'fixed', top: '20px', right: '20px', zIndex: 9999, color: 'white', padding: '10px 20px', borderRadius: '10px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

export default NouvelleReservation;